/**
 * Tailwind preset · clickt brand.
 *
 * Apps consume via:
 *   `presets: [require('@clickt/brand/tailwind-preset')]`
 *
 * Tokens are sourced from `./tokens.ts` so JS and CSS stay aligned. Marketing
 * (apps/marketing) and the dashboard (apps/dashboard) both extend this preset.
 */

import { colors, fontFamilies, motion, radii, shadows, typeScale } from './tokens.ts';

export const tailwindPreset = {
  // Class-strategy dark mode. Marketing never adds the `dark` class, so any
  // `dark:` utility compiled from shared components is inert there; the
  // dashboard toggles the class on <html>. Without this pin, Tailwind defaults
  // to `media` and dark-OS marketing visitors would pick up dashboard-only
  // dark styles.
  darkMode: 'class',
  theme: {
    extend: {
      colors: {
        // Semantic roles
        midnight: colors.midnight,
        'midnight-deep': colors.midnightDeep,
        electric: colors.electricBlue,
        sapphire: colors.deepSapphire,
        'cool-gray': colors.coolGray,
        'light-silver': colors.lightSilver,
        'border-subtle': colors.borderSubtle,
        success: colors.successGreen,
        warn: colors.alertOrange,
        danger: colors.signalRed,
        // Themeable surface role: white here, remapped to a CSS variable in
        // the dashboard's config so cards/panels flip in dark mode.
        surface: colors.white,
        // Always-dark role (tooltips, code blocks, scrims, inverse chips).
        // Stays midnight in both themes — never remap this one.
        ink: colors.midnight,

        // Convenience aliases for app-level work
        background: colors.white,
        foreground: colors.midnight,
        muted: colors.coolGray,
        primary: {
          DEFAULT: colors.electricBlue,
          hover: colors.deepSapphire,
          foreground: colors.white,
        },
        border: colors.lightSilver,
      },
      fontFamily: {
        sans: fontFamilies.sans.split(',').map((f) => f.trim()),
        display: fontFamilies.display.split(',').map((f) => f.trim()),
        mono: fontFamilies.mono.split(',').map((f) => f.trim()),
      },
      fontSize: {
        // Type scale per brand PDF §05
        'display-1': [
          typeScale.h1.size,
          { lineHeight: typeScale.h1.lineHeight, letterSpacing: typeScale.h1.letterSpacing },
        ],
        'display-2': [
          typeScale.h2.size,
          { lineHeight: typeScale.h2.lineHeight, letterSpacing: typeScale.h2.letterSpacing },
        ],
        'display-3': [
          typeScale.h3.size,
          { lineHeight: typeScale.h3.lineHeight, letterSpacing: typeScale.h3.letterSpacing },
        ],
        'display-4': [
          typeScale.h4.size,
          { lineHeight: typeScale.h4.lineHeight, letterSpacing: typeScale.h4.letterSpacing },
        ],
        'body-lg': [typeScale.bodyLg.size, { lineHeight: typeScale.bodyLg.lineHeight }],
        'body-md': [typeScale.body.size, { lineHeight: typeScale.body.lineHeight }],
        caption: [
          typeScale.caption.size,
          {
            lineHeight: typeScale.caption.lineHeight,
            letterSpacing: typeScale.caption.letterSpacing,
          },
        ],
      },
      borderRadius: {
        sm: radii.sm,
        DEFAULT: radii.md,
        md: radii.md,
        lg: radii.lg,
        pill: radii.pill,
        button: radii.button,
        input: radii.input,
        card: radii.card,
      },
      boxShadow: {
        soft: shadows.soft,
        hover: shadows.hover,
        'focus-ring': shadows.focusRing,
      },
      backgroundImage: {
        'gradient-primary': 'linear-gradient(135deg, #163FD6 0%, #2F5BFF 100%)',
        'gradient-premium-dark': 'linear-gradient(135deg, #020B1D 0%, #0F1D40 100%)',
        'gradient-glass-blue':
          'linear-gradient(135deg, rgba(47,91,255,0.15) 0%, rgba(22,63,214,0.05) 100%)',
      },
      transitionDuration: {
        fast: motion.fast,
        standard: motion.standard,
        large: motion.large,
      },
      transitionTimingFunction: {
        clickt: motion.easing,
      },
      screens: {
        sm: '375px',
        md: '768px',
        lg: '1024px',
        xl: '1280px',
        '2xl': '1440px',
      },
      keyframes: {
        'fade-in': {
          '0%': { opacity: '0', transform: 'translateY(8px)' },
          '100%': { opacity: '1', transform: 'translateY(0)' },
        },
        'count-up': {
          '0%': { opacity: '0' },
          '100%': { opacity: '1' },
        },
        'pulse-soft': {
          '0%, 100%': { opacity: '1' },
          '50%': { opacity: '0.55' },
        },
      },
      animation: {
        'fade-in': 'fade-in 400ms cubic-bezier(0.4, 0, 0.2, 1) both',
        'count-up': 'count-up 250ms cubic-bezier(0.4, 0, 0.2, 1) both',
        'pulse-soft': 'pulse-soft 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite',
      },
    },
  },
} as const;

export default tailwindPreset;
